import { createAsyncThunk } from '@reduxjs/toolkit';
import { DELIVERY_SALE_TYPES } from '../constants/saleType';

import {
  getStagedPurchases,
  getBufferPickupTime,
  getSaleType,
  getBufferDeliveryTime,
  getItems,
  getDayparts,
  getMenu,
  getAllCategoryInfos,
} from '../selectors';
import { getDayPartMenuConfig } from '../selectors/config';

import { setPickupTime } from '../operations';
import checkStagePurchaseAvailability from '../utils/ordering/checkStagePurchaseAvailability';

/**
 * # validateFutureMenuItems
 *
 * ### What it does:
 * - It checks if the items in your cart fit in to the time you have chosen in the future.
 * - It runs in when the user changes their pickup time / delivery time or when the user
 * enters the placeOrder screen.
 *
 * ### Scenarios:
 * - If it's:
 *    - Delivery: It would warn the user that these invalid items will be removed from the
 *                cart when you press confirm.
 *    - Catering: It would warn the user their original time, and their new time that they
 *                have chosen. When they press confirm the new time they picked
 *                (bufferPickupTime) will be applied and the items will be removed from the
 *                cart. They are also given the option to not apply this new time they have
 *                picked.
 *
 * ### Notes:
 * There is a thunk on both Web and Mobile under the same name that calls this thunk to
 * share the logic between both platforms but handle how the modal is shown differently.
 */
export const validateFutureMenuItems = createAsyncThunk(
  'validateFutureMenuItems',
  async (
    data: {
      onInvalidItemsExists: ({
        invalidItems,
        proceedWithChosenTime,
      }: {
        invalidItems: {
          id: string;
          name: string;
        }[];
        proceedWithChosenTime: () => void;
      }) => void;
      desiredDeliveryTime: string | null;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    const { onInvalidItemsExists, desiredDeliveryTime } = data;
    try {
      const state = getState() as EntireFrontendState;

      const saleType = getSaleType(state);
      const bufferPickupTime = getBufferPickupTime(state);
      const bufferDeliveryTime = getBufferDeliveryTime(state);
      const deliveryTime = desiredDeliveryTime || bufferDeliveryTime;

      const stagedPurchases = getStagedPurchases(state);
      const items = getItems(state);
      const daypartConfig = getDayPartMenuConfig(state);
      const daypartSlots = getDayparts(state);
      const menu = getMenu(state);
      const categoriesInfo = getAllCategoryInfos(state);

      const isDelivery = DELIVERY_SALE_TYPES.includes(saleType);

      const safeBufferPickupTime = bufferPickupTime || 'ASAP';
      const selectedScheduledTime = isDelivery
        ? deliveryTime
        : safeBufferPickupTime;

      const dateTimeSelected =
        selectedScheduledTime && selectedScheduledTime !== 'ASAP'
          ? selectedScheduledTime
          : undefined;

      const invalidItems = stagedPurchases
        .filter(purchase => {
          const available = checkStagePurchaseAvailability(
            purchase,
            items,
            daypartConfig,
            dateTimeSelected,
            daypartSlots,
            menu?.rootMenuNode?.subMenus,
            categoriesInfo,
          );
          return !available;
        })
        .map(({ id, item }) => ({ id, name: item.name }));

      // This is meant to be for non delivery orders (pickup and catering)
      const proceedWithChosenTime = () => {
        dispatch(setPickupTime(safeBufferPickupTime));
      };

      if (invalidItems.length) {
        onInvalidItemsExists({ invalidItems, proceedWithChosenTime });
      } else {
        proceedWithChosenTime();
      }
    } catch (e) {
      console.error(
        'Polygon Ordering: validateFutureMenuItems thunk failed',
        e,
      );
      return rejectWithValue(e);
    }
  },
);
